import { useEffect, useMemo, useRef } from "react";
import { useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { useDashboardWS } from "@/hooks/use-websocket";
import {
  useUnifiedNotificationsInfiniteQuery,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useClearAllNotifications,
  useNotificationPreferencesQuery,
  unifiedNotificationsKeys,
  type NotificationFilter,
  type UnifiedNotification,
  type UnifiedNotificationPage,
} from "@/lib/api/unified-notifications";

interface UseUnifiedNotificationsOptions {
  filter?: NotificationFilter;
  onNotification?: (notification: {
    type: string;
    title: string;
    message?: string;
  }) => void;
}

export function useUnifiedNotifications(
  options?: UseUnifiedNotificationsOptions
) {
  const queryClient = useQueryClient();
  const { addListener } = useDashboardWS();
  const filter = options?.filter ?? "all";
  const query = useUnifiedNotificationsInfiniteQuery(filter);
  const { data: prefs } = useNotificationPreferencesQuery();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const clearAll = useClearAllNotifications();
  const { onNotification } = options ?? {};
  const mutedTypesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    mutedTypesRef.current = new Set(prefs?.mutedTypes ?? []);
  }, [prefs?.mutedTypes]);
  const invalidateTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  const notifications = useMemo(
    () => query.data?.pages.flatMap((p) => p.data) ?? [],
    [query.data?.pages]
  );
  const unreadCount = query.data?.pages[0]?.unreadCount ?? 0;

  useEffect(() => {
    const remove = addListener((msg) => {
      if (msg.type === "notification:new") {
        if (mutedTypesRef.current.has(msg.notification.type)) return;

        const optimistic: UnifiedNotification = {
          id: msg.notification.id,
          scope: "guild",
          type: msg.notification.type,
          title: msg.notification.title,
          message: msg.notification.message,
          metadata: msg.notification.metadata,
          isRead: false,
          createdAt: msg.notification.createdAt,
          applicationGuildId: null,
          userId: null,
          tenantId: null,
        };

        // Update both filter cache keys
        for (const f of ["all", "unread"] as const) {
          queryClient.setQueryData<InfiniteData<UnifiedNotificationPage>>(
            unifiedNotificationsKeys.list(f),
            (old) => {
              if (!old?.pages.length) return old;
              const firstPage = old.pages[0]!;
              if (firstPage.data.some((n) => n.id === optimistic.id))
                return old;

              return {
                ...old,
                pages: [
                  {
                    ...firstPage,
                    data: [optimistic, ...firstPage.data],
                    unreadCount: firstPage.unreadCount + 1,
                  },
                  ...old.pages.slice(1),
                ],
              };
            }
          );
        }

        // Also update the lightweight count query
        queryClient.setQueryData(
          unifiedNotificationsKeys.count(),
          (old: { unreadCount: number } | undefined) => {
            if (!old) return old;
            return { unreadCount: old.unreadCount + 1 };
          }
        );

        // Debounced refetch for eventual consistency
        clearTimeout(invalidateTimerRef.current);
        invalidateTimerRef.current = setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: unifiedNotificationsKeys.all,
          });
        }, 2000);

        onNotification?.({
          type: msg.notification.type,
          title: msg.notification.title,
          message: msg.notification.message ?? undefined,
        });
      }
    });

    return () => {
      remove();
      clearTimeout(invalidateTimerRef.current);
    };
  }, [addListener, queryClient, onNotification]);

  return {
    notifications,
    unreadCount,
    isLoading: query.isLoading,
    markRead: markRead.mutate,
    markAllRead: () => markAllRead.mutate(),
    clearAll: () => clearAll.mutate(),
    isClearingAll: clearAll.isPending,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}
